import type { DailyListeningTimeData } from '@/services/listeningTime.service';

/** Hours thresholds that trigger celebrations. */
export const HOUR_MILESTONES: number[] = [
  10, 20, 50, 100,
  ...Array.from({ length: 40 }, (_, i) => 125 + i * 25), // 125, 150, 175 ... 1100
];

/** Minimum seconds per day to count toward a streak. */
export const STREAK_MIN_SECONDS = 300; // 5 minutes

/**
 * Returns ISO week key like "week:2026-W22".
 * Uses ISO 8601 week numbering (Monday = start of week).
 */
export function getISOWeekKey(date: Date): string {
  // Work entirely in UTC to avoid local-timezone shifts on UTC-midnight inputs
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const day = d.getUTCDay() || 7; // make Sunday = 7
  d.setUTCDate(d.getUTCDate() + 4 - day); // Thursday in current week
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `week:${d.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
}

/**
 * Returns the lowest HOUR_MILESTONES threshold crossed going from prevSeconds to
 * newSeconds, or null if none was crossed.
 */
export function findCrossedHourMilestone(
  prevSeconds: number,
  newSeconds: number,
): number | null {
  const prevHours = prevSeconds / 3600;
  const newHours = newSeconds / 3600;
  for (const milestone of HOUR_MILESTONES) {
    if (prevHours < milestone && newHours >= milestone) {
      return milestone;
    }
  }
  return null;
}

/**
 * Returns true if the 7 days ending on `todayStr` (YYYY-MM-DD) all have
 * at least STREAK_MIN_SECONDS of total listening time across all languages.
 */
export function isPerfectWeek(
  report: DailyListeningTimeData[],
  todayStr: string,
): boolean {
  // Aggregate seconds per date across languages
  const byDate = new Map<string, number>();
  for (const entry of report) {
    byDate.set(entry.date, (byDate.get(entry.date) ?? 0) + entry.totalSeconds);
  }

  // Build the 7-day window ending today using UTC arithmetic to avoid timezone-shift issues
  const [year, month, day] = todayStr.split('-').map(Number);
  for (let i = 0; i < 7; i++) {
    const d = new Date(Date.UTC(year, month - 1, day - i));
    const key = d.toISOString().slice(0, 10);
    if ((byDate.get(key) ?? 0) < STREAK_MIN_SECONDS) return false;
  }
  return true;
}
